import {Alert, Choice, IconButton, isFile, TextInput, Upload} from 'platform/components';
import {HStack, Link, Show, Text, VStack} from 'platform/foundation';

import {Field} from 'react-final-form';

import {useUploadFileMutation} from '@dms/api/base';
import {AutomarketplPlatformInputSchema, type AutomarketplPlatformInput} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, openFile} from 'shared/web';

import {getAdvertisingPreflightIssueText} from '../utils/getAdvertisingPreflightIssueText';
import {setAutomarketplInspectionAnswer} from '../utils/setAutomarketplInspectionAnswer';
import {getAutomarketplInspectionCategoryLabel} from './getAutomarketplInspectionCategoryLabel';

export type AutomarketplInputOptions = {
  inspection: readonly {
    categoryId: number;
    helperText?: string;
    label: string;
    value: number;
  }[];
};

type EditorProps = {
  isDisabled: boolean;
  options?: AutomarketplInputOptions;
  onUploadingChange: (isUploading: boolean) => void;
};
const answerOptions = () => [
  {label: i18n.t('general.labels.yes'), value: 1},
  {label: i18n.t('general.labels.no'), value: 0},
];

function validatePlatformInput(value: AutomarketplPlatformInput | undefined) {
  const result = AutomarketplPlatformInputSchema.safeParse(value ?? {});
  if (result.success) {
    return undefined;
  }
  return result.error.issues.map((issue) =>
    getAdvertisingPreflightIssueText({
      kind: 'field',
      reasonCode: 'invalid-source-format',
      sourcePaths: [`source.automarketpl.${issue.path.join('.')}`],
      mappingIdentifier: null,
      targetPath: null,
      blockingDimension: null,
    })
  );
}

export function AutomarketplInputEditor(props: EditorProps) {
  return (
    <Field<AutomarketplPlatformInput> name="platformInput" validate={validatePlatformInput}>
      {({meta}) => (
        <VStack spacing={4}>
          <Show when={!!meta.error && (meta.dirty || meta.submitFailed)}>
            <Alert variant="error" type="inline" message={meta.error} />
          </Show>
          <Field<string | null> name="platformInput.rozmiarOgumienia">
            {({input}) => (
              <TextInput
                label={`${i18n.t('entity.tireSet.labels.tires')} · ${i18n.t('entity.vehicle.labels.dimensions')}`}
                value={input.value}
                onChange={input.onChange}
                isDisabled={props.isDisabled}
              />
            )}
          </Field>
          <SpecificationEditor {...props} />
          <InspectionEditor {...props} />
        </VStack>
      )}
    </Field>
  );
}

const specificationLabel = `${i18n.t('entity.newVehicle.labels.baseSpecification')} (PDF)`;

function SpecificationEditor(props: EditorProps) {
  const [uploadFile] = useUploadFileMutation();
  return (
    <Field<string | undefined> name="platformInput.zalacznikSpecyfikacja">
      {({input: {value: fileUri, onChange}}) =>
        fileUri ? (
          <HStack spacing={2} align="center">
            <Link onClick={() => openFile(fileUri)} size="small">
              {specificationLabel}
            </Link>
            <IconButton
              icon="action/delete"
              size="small"
              onClick={() => onChange(undefined)}
              isDisabled={props.isDisabled}
            />
          </HStack>
        ) : (
          <Upload
            type="button"
            buttonVariant="ghostLink"
            uploadIcon="file/upload"
            uploadText={specificationLabel}
            accept="application/pdf"
            isDisabled={props.isDisabled}
            customRequest={({file, onSuccess, onError}) => {
              if (!isFile(file)) {
                return;
              }
              props.onUploadingChange(true);
              uploadFile({file})
                .unwrap()
                .then((data) => {
                  onChange(data.fileUri);
                  onSuccess?.(data);
                })
                .catch((error: ApiError) => {
                  handleApiError(error);
                  onError?.(new Error());
                })
                .finally(() => props.onUploadingChange(false));
            }}
          />
        )
      }
    </Field>
  );
}

function InspectionEditor(props: EditorProps) {
  return (
    <VStack spacing={4}>
      <Text alternative>{i18n.t('module.inspection.title')}</Text>
      <Show when={!props.options?.inspection.length}>
        <Text>{i18n.t('entity.workshop.labels.notAvailable')}</Text>
      </Show>
      <Field<AutomarketplPlatformInput['inspekcja']>
        name="platformInput.inspekcja"
        format={(value) => value}
      >
        {({input}) =>
          props.options?.inspection.map((question, index, questions) => {
            const answer = input.value?.find((item) => item.pytanieId === question.value);
            return (
              <VStack key={question.value} spacing={3}>
                <Show
                  when={index === 0 || questions[index - 1]?.categoryId !== question.categoryId}
                >
                  <Text alternative>
                    {getAutomarketplInspectionCategoryLabel(question.categoryId)}
                  </Text>
                </Show>
                <Choice
                  label={question.label}
                  helperText={question.helperText}
                  value={answer?.wartosc ?? null}
                  options={answerOptions()}
                  onChange={(value) =>
                    input.onChange(
                      setAutomarketplInspectionAnswer(input.value, question.value, value)
                    )
                  }
                  isDisabled={props.isDisabled}
                  menuInPortal
                />
                <Show when={answer?.wartosc === 0}>
                  <TextInput
                    label={i18n.t('general.labels.note')}
                    value={answer?.uwagi ?? ''}
                    onChange={(value) =>
                      input.onChange(
                        setAutomarketplInspectionAnswer(input.value, question.value, 0, value ?? '')
                      )
                    }
                    isRequired
                    isDisabled={props.isDisabled}
                  />
                </Show>
              </VStack>
            );
          })
        }
      </Field>
    </VStack>
  );
}
